<template>
  <div id="request-form" class="w-full">
    <div class="form-wrapper form-wrapper-select md:w-2/3">
      <select v-model="activeRequest" class="w-full appearance-none">
        <option :value="null" selected>
          Please select a change request...
        </option>
        <optgroup
          v-for="(request, index) in requests"
          :key="index"
          :label="request.category"
        >
          <option
            v-for="option in request.options"
            :key="option.id"
            :value="option.id"
          >
            {{ option.request_type }}
          </option>
        </optgroup>
      </select>
    </div>
    <collapse-transition v-if="requestDetails">
      <form class="mb-12" @submit.prevent="sendRequest" novalidate>
        <div class="p-6 pb-0">
          <h2 class="text-lg mb-2">
            {{ requestDetails.category }}: {{ requestDetails.request_type }}
          </h2>
          <p>
            <b>Requirements:</b>
            {{ requestDetails.requirements }}
          </p>
        </div>
        <template v-if="requestDetails.template == 'staff:new'">
          <div class="flex flex-wrap">
            <input-default
              :error="getError('data', 'name')"
              :input="schema.name"
              :application-value="requestForm.data.name"
              @update:input="d => updateValue(d, 'name')"
            />
            <input-default
              :error="getError('data', 'job_title')"
              :input="schema.job_title"
              :application-value="requestForm.data.job_title"
              @update:input="d => updateValue(d, 'job_title')"
            />
            <div
              class="w-2/3 border-t pt-3 -mt-3"
              style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem;"
            >
              <input-default
                :error="getError('data', 'salary')"
                :input="schema.salary"
                :application-value="requestForm.data.salary"
                @update:input="d => updateValue(d, 'salary')"
              />
              <input-default
                :error="getError('data', 'employer_nic')"
                :input="schema.employer_nic"
                :application-value="requestForm.data.employer_nic"
                @update:input="d => updateValue(d, 'employer_nic')"
              />
              <input-default
                :error="getError('data', 'employer_pension')"
                :input="schema.employer_pension"
                :application-value="requestForm.data.employer_pension"
                @update:input="d => updateValue(d, 'employer_pension')"
              />
              <input-default
                :error="getError('data', 'medical_health_premiums')"
                :input="schema.medical_health_premiums"
                :application-value="requestForm.data.medical_health_premiums"
                @update:input="d => updateValue(d, 'medical_health_premiums')"
              />
              <input-default
                :error="getError('data', 'life_assurance_premiums')"
                :input="schema.life_assurance_premiums"
                :application-value="requestForm.data.life_assurance_premiums"
                @update:input="d => updateValue(d, 'life_assurance_premiums')"
              />
            </div>
            <div
              class="w-1/3 bg-grey-100 p-6 rounded -mt-3 border-t"
              style="display: grid; gap: 0.5rem;"
            >
              <input-default
                :input="schema.gross_employee_cost"
                :application-value="gecForStaff.toFixed(2)"
              />
              <input-default
                :input="schema.day_rate"
                :application-value="(gecForStaff / 225).toFixed(2)"
              />
              <input-default
                :input="schema.hourly_rate"
                :application-value="(gecForStaff / 225 / 8).toFixed(2)"
              />
            </div>
            <input-multiline
              :input="requestTextInput"
              parent-field="change_request"
              :error="getError('data', 'message')"
              :application-value="requestForm.data.message"
              @update:input="d => updateValue(d, 'message')"
            />
          </div>
        </template>
        <template v-else-if="requestDetails.template == 'staff:edit'">
          <div class="flex flex-wrap">
            <input-select
              :input="{
                key: 'staff_id',
                type: 'select',
                label: 'Staff Member',
                instructions: null,
                attributes: {
                  placeholder: null,
                  default: null,
                  prepend: null,
                  append: null,
                  locked: loading,
                  required: true
                },
                wrapper: {
                  width: 'full'
                },
                validation: {
                  min: false,
                  max: false,
                  limit: false
                }
              }"
              :error="getError('data', 'staff_id')"
              :application-value="requestForm.data.staff_id"
              @update:input="d => updateValue(d, 'staff_id')"
            >
              <option disabled :value="null">
                Please select a staff member...
              </option>
              <option
                v-for="s in availableStaff"
                :key="s.value"
                :value="s.value"
              >
                {{ s.text }}
              </option>
            </input-select>
            <dl v-if="requestForm.data.staff_id" class="definition-table">
              <dt>Gross Annual Salary</dt>
              <dd>£{{ selectStaffMemberRate.salary }}</dd>
              <dt>Employer NIC</dt>
              <dd>£{{ selectStaffMemberRate.employer_nic }}</dd>
              <dt>Employer Pension</dt>
              <dd>£{{ selectStaffMemberRate.employer_pension }}</dd>
              <dt>Medical Health Premiums</dt>
              <dd>£{{ selectStaffMemberRate.medical_health_premiums }}</dd>
              <dt>Life Assurance Premiums</dt>
              <dd>£{{ selectStaffMemberRate.life_assurance_premiums }}</dd>
              <dt>Gross Employee Cost</dt>
              <dd>£{{ selectStaffMemberRate.gross_employee_cost }}</dd>
              <dt>Breakdown</dt>
              <dd>£{{ selectStaffMemberRate.day_rate }} per day / £{{ selectStaffMemberRate.hourly_rate }} per hour</dd>
            </dl>
            <div
              class="w-2/3 border-t pt-3 -mt-3"
              style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem;"
            >
              <input-default
                :error="getError('data', 'salary')"
                :input="schema.salary"
                :application-value="requestForm.data.salary"
                @update:input="d => updateValue(d, 'salary')"
              />
              <input-default
                :error="getError('data', 'employer_nic')"
                :input="schema.employer_nic"
                :application-value="requestForm.data.employer_nic"
                @update:input="d => updateValue(d, 'employer_nic')"
              />
              <input-default
                :error="getError('data', 'employer_pension')"
                :input="schema.employer_pension"
                :application-value="requestForm.data.employer_pension"
                @update:input="d => updateValue(d, 'employer_pension')"
              />
              <input-default
                :error="getError('data', 'medical_health_premiums')"
                :input="schema.medical_health_premiums"
                :application-value="requestForm.data.medical_health_premiums"
                @update:input="d => updateValue(d, 'medical_health_premiums')"
              />
              <input-default
                :error="getError('data', 'life_assurance_premiums')"
                :input="schema.life_assurance_premiums"
                :application-value="requestForm.data.life_assurance_premiums"
                @update:input="d => updateValue(d, 'life_assurance_premiums')"
              />
            </div>
            <div
              class="w-1/3 bg-grey-100 p-6 rounded -mt-3 border-t"
              style="display: grid; gap: 0.5rem;"
            >
              <input-default
                :input="schema.gross_employee_cost"
                :application-value="gecForStaff.toFixed(2)"
              />
              <input-default
                :input="schema.day_rate"
                :application-value="(gecForStaff / 225).toFixed(2)"
              />
              <input-default
                :input="schema.hourly_rate"
                :application-value="(gecForStaff / 225 / 8).toFixed(2)"
              />
            </div>
            <input-multiline
              :input="requestTextInput"
              parent-field="change_request"
              :error="getError('data', 'message')"
              :application-value="requestForm.data.message"
              @update:input="d => updateValue(d, 'message')"
            />
          </div>
        </template>
        <template v-else-if="requestDetails.template == 'bank'">
          <input-default
            :input="{
              key: 'account_name',
              type: 'text',
              label: 'Account Name',
              instructions: null,
              attributes: {
                placeholder: 'Account Name',
                default: null,
                prepend: null,
                append: null,
                locked: loading,
                required: true
              },
              wrapper: {
                width: 'full'
              },
              validation: {
                min: false,
                max: false,
                limit: false
              }
            }"
            :error="getError('data', 'account_name')"
            :application-value="requestForm.data.account_name"
            @update:input="d => updateValue(d, 'account_name')"
          />
          <input-default
            :input="{
              key: 'sort_code',
              type: 'text',
              label: 'Sort Code',
              instructions: null,
              attributes: {
                placeholder: 'xxxxxx',
                default: null,
                prepend: null,
                append: null,
                locked: loading,
                required: true
              },
              wrapper: {
                width: '1/3'
              },
              validation: {
                min: false,
                max: false,
                limit: 6
              }
            }"
            :error="getError('data', 'sort_code')"
            :application-value="requestForm.data.sort_code"
            @update:input="d => updateValue(d, 'sort_code')"
          />
          <input-default
            :input="{
              key: 'account_number',
              type: 'text',
              label: 'Account Number',
              instructions: null,
              attributes: {
                placeholder: 'xxxxxxxx',
                default: null,
                prepend: null,
                append: null,
                locked: loading,
                required: true
              },
              wrapper: {
                width: '2/3'
              },
              validation: {
                min: false,
                max: false,
                limit: 8
              }
            }"
            :error="getError('data', 'account_number')"
            :application-value="requestForm.data.account_number"
            @update:input="d => updateValue(d, 'account_number')"
          />
          <input-default
            :input="{
              key: 'bank_name',
              type: 'text',
              label: 'Bank Name',
              instructions: null,
              attributes: {
                placeholder: 'e.g. HSBC',
                default: null,
                prepend: null,
                append: null,
                locked: loading,
                required: true
              },
              wrapper: {
                width: 'full'
              },
              validation: {
                min: false,
                max: false,
                limit: false
              }
            }"
            :error="getError('data', 'bank_name')"
            :application-value="requestForm.data.bank_name"
            @update:input="d => updateValue(d, 'bank_name')"
          />
          <input-default
            :input="{
              key: 'bank_address',
              type: 'text',
              label: 'Bank Address',
              instructions: null,
              attributes: {
                placeholder: null,
                default: null,
                prepend: null,
                append: null,
                locked: loading,
                required: true
              },
              wrapper: {
                width: 'full'
              },
              validation: {
                min: false,
                max: false,
                limit: false
              }
            }"
            :error="getError('data', 'bank_address')"
            :application-value="requestForm.data.bank_address"
            @update:input="d => updateValue(d, 'bank_address')"
          />
          <input-multiline
            :input="requestTextInput"
            parent-field="change_request"
            :error="getError('data', 'message')"
            :application-value="requestForm.data.message"
            @update:input="d => updateValue(d, 'message')"
          />
        </template>
        <input-multiline
          v-else
          :input="requestTextInput"
          parent-field="change_request"
          :error="getError('data')"
          :application-value="requestForm.data.message"
          @update:input="d => updateValue(d, 'message')"
        />
        <input-upload
          v-if="requestDetails.upload_required"
          :input="requestUpload"
          parent-field="change_request"
          :error="getError('file_id')"
          :application-value="requestForm.upload"
          @update:input="updateFiles"
        />
        <div class="mx-6 max-w-xs">
          <app-button type="submit" :disabled="loading || !canRequest">
            Send Request
          </app-button>
        </div>
      </form>
    </collapse-transition>
  </div>
</template>

<script>
import { CollapseTransition } from "vue2-transitions";
export default {
  name: "RequestForm",
  components: {
    CollapseTransition
  },
  props: {
    canRequest: {
      type: Boolean,
      required: false,
      default: false
    },
    staff: {
      type: Array,
      required: false,
      default: null
    },
    requests: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      activeRequest: null,
      formErrors: {},
      requestForm: {
        data: null,
        upload: []
      }
    };
  },
  computed: {
    gecForStaff() {
      return parseFloat(this.requestForm.data?.salary ?? 0) +
              parseFloat(this.requestForm.data?.employer_nic ?? 0) +
              parseFloat(this.requestForm.data?.employer_pension ?? 0)  +
              parseFloat(this.requestForm.data?.medical_health_premiums ?? 0) +
              parseFloat(this.requestForm.data?.life_assurance_premiums ?? 0)
    },

    selectStaffMemberRate() {
      const defaultRate = {
        salary: 0,
        employer_nic: 0,
        employer_pension: 0,
        medical_health_premiums: 0,
        life_assurance_premiums: 0,
        gross_employee_cost: 0,
        day_rate: 0,
        hourly_rate: 0
      };

      if (!this.requestForm.data?.staff_id) return defaultRate;

      const staffMember = this.staff.find(s => s.id == this.requestForm.data.staff_id);
      if (!staffMember) return defaultRate;
      const { gross_annual_salary, employer_nic, employer_pension, medical_health_premiums, life_assurance_premiums, gross_employee_cost, day_rate, hourly_rate } = staffMember.active_rate;
      return {
        salary: gross_annual_salary.toLocaleString(),
        employer_nic: employer_nic.toLocaleString(),
        employer_pension: employer_pension.toLocaleString(),
        medical_health_premiums: medical_health_premiums.toLocaleString(),
        life_assurance_premiums: life_assurance_premiums.toLocaleString(),
        gross_employee_cost: gross_employee_cost.toLocaleString(),
        day_rate: day_rate.toLocaleString(),
        hourly_rate: hourly_rate.toLocaleString(),
      };
    },
    allRequests() {
      return this.requests.map(request => request.options).flat();
    },
    requestDetails() {
      if (!this.activeRequest) return;
      window.location.hash = "request-form";
      return this.allRequests.find(
        request => request.id === this.activeRequest
      );
    },
    locked() {
      return this.loading || !this.canRequest;
    },
    availableStaff() {
      if (!this.staff) return [];
      return this.staff.map(staff => ({
        value: staff.id,
        text: staff.name + (staff.job_title ? ` - ${staff.job_title}` : "")
      }));
    },
    requestTextInput() {
      return {
        key: "message",
        type: "multiline",
        label: "Request Explanation",
        wrapper: { width: "full" },
        attributes: {
          append: "",
          locked: this.locked,
          default: "",
          prepend: "",
          required: true,
          placeholder: ""
        },
        validation: { max: false, min: false, limit: 100 },
        instructions: ""
      };
    },
    requestUpload() {
      return {
        key: this.requestDetails.id,
        type: "upload",
        label: "Evidence Document",
        wrapper: { width: "full" },
        attributes: {
          append: "",
          locked: this.locked,
          default: [],
          prepend: "",
          multiple: false,
          required: false,
          template: null,
          placeholder: ""
        },
        validation: {
          max: 1,
          min: 1,
          limit: 5,
          accept: [".pdf", ".xls", ".xlsx", ".doc", ".docx"]
        },
        instructions: ""
      };
    },
    schema() {
      return {
        name: {
          key: "name",
          type: "text",
          label: "Name",
          instructions: null,
          attributes: {
            placeholder: "Jane Doe",
            default: null,
            prepend: null,
            append: null,
            locked: this.loading,
            required: true
          },
          wrapper: {
            width: "1/2"
          },
          validation: {
            min: false,
            max: false,
            limit: false
          }
        },
        job_title: {
          key: "job_title",
          type: "text",
          label: "Job Title",
          instructions: null,
          attributes: {
            placeholder: "Project Manager",
            default: null,
            prepend: null,
            append: null,
            locked: this.loading,
            required: false
          },
          wrapper: {
            width: "1/2"
          },
          validation: {
            min: false,
            max: false,
            limit: false
          }
        },
        salary: {
          key: "salary",
          type: "number",
          label: "Salary",
          instructions: null,
          attributes: {
            placeholder: "45000.00",
            default: null,
            append: null,
            locked: this.loading,
            required: true,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        },
        employer_nic: {
          key: "employer_nic",
          type: "number",
          label: "Employer NIC",
          instructions: null,
          attributes: {
            placeholder: "6500.00",
            default: null,
            append: null,
            locked: this.loading,
            required: true,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        },
        employer_pension: {
          key: "employer_pension",
          type: "number",
          label: "Annual Employer Pension Contribution",
          instructions: null,
          attributes: {
            placeholder: "4500.00",
            default: null,
            append: null,
            locked: this.loading,
            required: true,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        },
        medical_health_premiums: {
          key: "medical_health_premiums",
          type: "number",
          label: "Annual Medical/Health Insurance Premiums",
          instructions: null,
          attributes: {
            placeholder: "2500.00",
            default: 0,
            append: null,
            locked: this.loading,
            required: true,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        },
        life_assurance_premiums: {
          key: "life_assurance_premiums",
          type: "number",
          label: "Annual Life Assurance Premiums",
          instructions: null,
          attributes: {
            placeholder: "2500.00",
            default: 0,
            append: null,
            locked: this.loading,
            required: true,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        },
        gross_employee_cost: {
          key: "gross_employee_cost",
          type: "number",
          label: "Gross Employee Cost",
          instructions: null,
          attributes: {
            placeholder: "55000.00",
            default: null,
            append: null,
            locked: true,
            required: false,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 500000,
            step: 0.01,
            limit: false
          }
        },
        hourly_rate: {
          key: "hourly_rate",
          type: "number",
          label: "Hourly Rate",
          instructions: null,
          attributes: {
            placeholder: "45.00",
            default: null,
            append: null,
            locked: true,
            required: false,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        },
        day_rate: {
          key: "day_rate",
          type: "number",
          label: "Day Rate",
          instructions: null,
          attributes: {
            placeholder: "450.00",
            default: null,
            append: null,
            locked: true,
            required: false,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        }
      };
    }
  },
  watch: {
    activeRequest(value) {
      if (!value) return this.resetForm();
      this.setTemplate(this.requestDetails.template);
    }
  },
  methods: {
    getError(key, index = null) {
      if (index !== null) {
        return this.formErrors[`${key}.${index}`] ?? [];
      }
      return this.formErrors[key] ?? [];
    },
    sendRequest() {
      if (!this.canRequest) return;
      this.loading = true;
      const file = this.requestForm.upload[0];
      axios
        .post(window.location.href, {
          option_id: this.requestDetails.id,
          data: this.requestForm.data,
          file_id: file ? file.id : null
        })
        .then(response => {
          this.resetForm();
          return this.$emit("request:log", response.data);
        })
        .catch(err => {
          this.formErrors = err.response?.data?.errors ?? {};
        })
        .then(() => {
          this.loading = false;
        });
    },
    updateValue({ value }, key = null, parser = v => v) {
      if (key) {
        this.requestForm.data[key] = parser(value);
        return;
      }
      this.requestForm.data = parser;
    },
    updateFiles({ value }) {
      this.requestForm.upload = value;
    },
    resetForm() {
      this.activeRequest = null;
      this.formErrors = {};
      this.requestForm.data = null;
      this.requestForm.upload = [];
    },
    setTemplate(template) {
      switch (template) {
        case "staff:new":
          this.requestForm.data = Object.assign({}, {
            name: "",
            job_title: "",
            salary: 0,
            employer_nic: 0,
            employer_pension: 0,
            medical_health_premiums: 0,
            life_assurance_premiums: 0,
            message: ""
          });
          return;
        case "staff:edit":
          this.requestForm.data = Object.assign({}, {
            staff_id: null,
            salary: 0,
            employer_nic: 0,
            employer_pension: 0,
            medical_health_premiums: 0,
            life_assurance_premiums: 0,
            message: ""
          })
          return;
        case "bank":
          this.$set(this.requestForm, "data", {
            account_name: "",
            account_number: "",
            bank_name: "",
            bank_address: "",
            sort_code: "",
            message: ""
          });
          return;
        default:
          this.$set(this.requestForm, "data", {
            message: ""
          });
      }
    },
    inputGenerator(
      key,
      label,
      locked,
      type = "text",
      wrapper = { width: "full" },
      validation = {},
      required = false
    ) {
      return {
        key,
        type,
        label,
        wrapper,
        attributes: {
          append: "",
          locked,
          default: "",
          prepend: "",
          required,
          placeholder: ""
        },
        validation,
        instructions: ""
      };
    }
  }
};
</script>

<style scoped>
.definition-table {
  @apply w-full mb-8 border;
  display: grid;
  grid-template-columns: auto 1fr;
}

.definition-table dt {
  @apply font-semibold text-left;
}

.definition-table dt,
.definition-table dd {
  @apply border;
}
</style>
